/**
 * Shared UI design tokens — the single source of truth for chrome
 * (overlays, panels, modals, chips, buttons). Mirrors the design
 * treatment (`treatments/bjjmap — Treatment & Vision.dc.html`, §04–§07):
 *
 *   - Surfaces: void → panel → raised, each a step lighter.
 *   - Hairlines: one thin line (LINE) and a slightly stronger one (LINE2).
 *   - Ink ramp: primary → secondary → muted/tracked.
 *   - Fonts: display (Space Grotesk), ui (Hanken Grotesk), mono (JetBrains).
 *   - Radius: a tight range — SM for small controls, LG for panels/modals.
 *
 * NOTE ON SCOPE: node / edge / zone / cluster colours live in
 * `src/zones/palette.ts` and remain the source of truth for anything
 * drawn in the 3D scene. This module is only for DOM chrome. The two
 * agree on the shared accent hexes (gold, blue, sand, …) by value.
 */

// ----- Fonts (resolve to the CSS vars declared in index.html) -----
export const FONT = {
  display: 'var(--bjjmap-font-display)',
  ui: 'var(--bjjmap-font-ui)',
  mono: 'var(--bjjmap-font-mono)',
} as const;

// ----- Surfaces -----
export const SURFACE = {
  void: '#070810', // the canvas — everything floats on it
  panel: '#0e1017', // sidebar & sticky chrome
  raised: '#141824', // cards, modals, popovers
} as const;

// Translucent variants of panel/raised for glass overlays that sit over
// the live canvas (keep a hint of the constellation behind them).
export const GLASS = {
  panel: 'rgba(14,16,23,0.92)',
  raised: 'rgba(20,24,36,0.96)',
  // More see-through variant for cards that float over the live map
  // (onboarding coach, tutorial) so they don't blanket the screen —
  // especially on mobile. Pair with a stronger backdrop blur.
  raisedSoft: 'rgba(20,24,36,0.8)',
} as const;

// ----- Hairlines -----
export const LINE = 'rgba(255,255,255,0.08)';
export const LINE2 = 'rgba(255,255,255,0.14)';

// ----- Ink ramp -----
export const INK = '#e9ecf1'; // primary body / titles
export const INK2 = '#9aa1ac'; // secondary
export const INK3 = '#646b78'; // muted / tracked labels

// ----- Accents (shared with palette.ts by value) -----
export const GOLD = '#e0b24a'; // brand + primary action ONLY
export const GOLD_INK = '#1a1607'; // text on a gold fill
export const BLUE = '#7aaec0'; // bottom / intermediate
export const SAND = '#d4a070'; // top / advanced
export const GREY = '#b3b6bc'; // neutral
export const FLUX = '#e0c468'; // transitional
export const TERM = '#a48eb0'; // terminal / game over
export const GREEN = '#6fa06b'; // fundamental
export const VIOLET = '#9b7fc4'; // specialist

// ----- Radius (10–14 small, 16–18 large) -----
export const RADIUS = {
  sm: 10,
  md: 12,
  lg: 16,
  pill: 999,
} as const;

// ----- Composed helpers -----

/** Standard glass panel: raised translucent fill + hairline + large radius. */
export const panelSurface = {
  background: GLASS.raised,
  border: `1px solid ${LINE2}`,
  borderRadius: RADIUS.lg,
} as const;

/** Gold primary button (the ONE place brand gold is spent on chrome). */
export const primaryButton: React.CSSProperties = {
  fontFamily: FONT.ui,
  fontWeight: 600,
  fontSize: 14,
  color: GOLD_INK,
  background: GOLD,
  border: 0,
  borderRadius: RADIUS.sm,
  padding: '10px 16px',
  cursor: 'pointer',
};

/** Secondary (raised) button. */
export const secondaryButton: React.CSSProperties = {
  fontFamily: FONT.ui,
  fontWeight: 500,
  fontSize: 14,
  color: INK,
  background: SURFACE.raised,
  border: `1px solid ${LINE2}`,
  borderRadius: RADIUS.sm,
  padding: '10px 16px',
  cursor: 'pointer',
};

/**
 * A tinted "active pill" background/border pair, given an accent hex.
 * Controls are quiet when inactive, tinted-with-hairline when active —
 * the treatment's §07 rule.
 */
export function activePill(accent: string): React.CSSProperties {
  return {
    color: INK,
    background: hexA(accent, 0.14),
    border: `1px solid ${hexA(accent, 0.32)}`,
  };
}

/** Hex (#rrggbb) → rgba() string at the given alpha. */
export function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
